import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useHelp } from '../contexts/HelpContext';
import './helpDrawer.css';

const MIN_WIDTH = 320;
const MAX_WIDTH = 900;
const DEFAULT_WIDTH = 520;
const WIDTH_KEY = 'helpDrawerWidth';

const HelpDrawer = () => {
  const { helpSlug, isOpen, closeHelp } = useHelp();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [width, setWidth] = useState(() => {
    const stored = parseInt(localStorage.getItem(WIDTH_KEY), 10);
    if (Number.isNaN(stored)) return DEFAULT_WIDTH;
    return Math.min(Math.max(stored, MIN_WIDTH), MAX_WIDTH);
  });
  const resizingRef = useRef(false);

  const fetchDocument = useCallback(async () => {
    if (!helpSlug || !isOpen) return;
    setLoading(true);
    setError('');
    try {
      const response = await api.get(`/help/document/${helpSlug}`);
      setDocumentData(response.data.document);
    } catch (err) {
      setDocumentData(null);
      setError(err.response?.data?.message || 'Help document not found.');
    } finally {
      setLoading(false);
    }
  }, [helpSlug, isOpen]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!resizingRef.current) return;
      const nextWidth = Math.min(
        Math.max(window.innerWidth - e.clientX, MIN_WIDTH),
        Math.min(MAX_WIDTH, window.innerWidth - 40)
      );
      setWidth(nextWidth);
    };
    const handleUp = () => {
      if (resizingRef.current) {
        resizingRef.current = false;
        localStorage.setItem(WIDTH_KEY, String(width));
      }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [width]);

  if (!isOpen) return null;

  return (
    <>
      <div className="help-drawer-overlay" onClick={closeHelp} aria-hidden="true" />
      <div className="help-drawer-panel" style={{ width }} role="dialog" aria-label="Help">
        <div
          className="help-drawer-handle"
          onMouseDown={() => { resizingRef.current = true; }}
          aria-hidden="true"
        />
        <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between bg-slate-900 text-white">
          <div>
            <p className="text-sm uppercase tracking-wide text-slate-300">Help</p>
            <h2 className="text-lg font-semibold">{documentData?.title || 'Help Center'}</h2>
          </div>
          <button
            type="button"
            onClick={closeHelp}
            className="text-slate-300 hover:text-white transition text-2xl leading-none"
            aria-label="Close help"
          >
            ×
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {loading && <div className="text-sm text-slate-500">Loading help content...</div>}
          {!loading && error && (
            <div className="text-sm text-rose-600">
              {error}
              <div className="mt-3">
                <a className="text-primary-600 hover:underline" href="/help">Go to Help Center</a>
              </div>
            </div>
          )}
          {!loading && !error && documentData && (
            <div
              className="help-content"
              dangerouslySetInnerHTML={{ __html: documentData.contentHtml }}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default HelpDrawer;
